import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BulkSelectEmployeeDialogComponent, DeprecatedConfirmationDialogComponent } from '@app/components';
import { BaseForm } from '@app/modules/forms/base.form';
import { Status } from '@app/modules/performance/enums/status.enum';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { ShowView } from '@common/views';
import { Employee } from '@models/employee/employee.model';
import { GoalUpdate } from '@models/performance/goal-update.model';
import { PerformanceResolver } from '@performance/performance.resolver';
import moment from 'moment';

@Component({
    templateUrl: './goal-show.template.html',
    styleUrls: ['./goal-show.style.scss'],
})
export class GoalShowView extends ShowView implements OnInit {
    @ViewChild('confirmDelete', { static: true }) confirmDelete: DeprecatedConfirmationDialogComponent;
    @ViewChild('confirmUpdateDelete', { static: true }) confirmUpdateDelete: DeprecatedConfirmationDialogComponent;
    @ViewChild('confirmComplete', { static: true }) confirmComplete: DeprecatedConfirmationDialogComponent;
    @ViewChild('confirmMissed', { static: true }) confirmMissed: DeprecatedConfirmationDialogComponent;
    @ViewChild('appEmployeeSelection', { static: true }) appEmployeeSelection: BulkSelectEmployeeDialogComponent;

    isLoading = true;

    Status = Status;

    goal: any = {};

    update: GoalUpdate = new GoalUpdate({
        metricIncrement: true,
        metricMilestones: null,
        offTrack: false,
        progressing: true,
        onTrack: false,
    });

    sharedPercent = 0;
    individualPercent = 0;
    individualProgress: any = {};

    isCreator = false;
    canAdministrate = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private element: ElementRef,
        public auth: AuthService,
        private notify: NotifyService,
        private resolver: PerformanceResolver
    ) {
        super();
        this.goal = this.route.snapshot.data.models.get('goal');

        if (this.goal.metricType === 'milestones') {
            this.parseMilestones();
        }
        this.checkPermission();
    }

    checkPermission(): void {
        const isCompany = this.auth.company.id === this.goal.companyId;
        if (!isCompany) {
            this.router.navigate(['/forbidden']);
        }

        this.isCreator = this.auth.employee.id === this.goal.createdBy;
        this.canAdministrate = this.auth.isAdmin();
    }

    ngOnInit(): void {
        this.getIndividualProgress();
    }

    reResolve(): void {
        this.isLoading = true;
        this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot).then((models) => {
            this.goal = models.get('goal');
            this.getIndividualProgress();

            this.update = new GoalUpdate({
                metricIncrement: true,
                metricMilestones: null,
                offTrack: false,
                progressing: true,
                onTrack: false,
            });
            this.parseMilestones();

            this.isLoading = false;
        });
    }

    getIndividualProgress(): void {
        if (!this.goal.isShared && this.goal.userIsParticipant) {
            const progress = this.goal.individualProgress;

            if (this.goal.metricType !== 'milestones') {
                this.individualProgress = progress;
            }
            if (this.goal.metricType === 'milestones') {
                this.individualProgress.currentValue = progress.currentValue.currentValue;
                this.goal.currentMilestones = progress.currentValue.currentMilestones || [];
            }
            this.renderProgress();
            this.isLoading = false;
        } else {
            this.renderProgress();
            this.isLoading = false;
        }
    }

    onSave(form?: BaseForm): void {
        this.isLoading = true;
        this.goal
            .save()
            .then(() => {
                this.reResolve();
                form?.reset();
                this.notify.success('performance.goal-show.saved');
            })
            .catch((err) => {
                this.notify.error(err);
                this.isLoading = false;
            });
    }

    onCancel(): void {
        this.goal.revert();
    }

    onAssign(): void {
        this.appEmployeeSelection.show<Employee[]>([...this.goal.participants]).then((employees) => {
            if (employees) {
                this.goal.sync('participants', employees);
                this.onSave();
            }
        });
    }

    onForceComplete(): void {
        setTimeout(() => {
            this.confirmComplete.show().then((confirmDelete) => {
                if (confirmDelete) {
                    this.updateGoalSetCompletedAt();
                }
            });
        });
    }

    onForceMissed(): void {
        setTimeout(() => {
            this.confirmMissed.show().then((confirmDelete) => {
                if (confirmDelete) {
                    this.updateGoalSetCompletedAt();
                }
            });
        });
    }

    onDeleteUpdate(goalUpdate: GoalUpdate): void {
        setTimeout(() => {
            this.confirmUpdateDelete.show().then((confirmDelete) => {
                if (confirmDelete) {
                    this.isLoading = true;
                    goalUpdate
                        .param('goal', this.goal.id)
                        .delete()
                        .then(() => {
                            this.reResolve();
                        })
                        .catch((err) => {
                            this.notify.error(err);
                            this.isLoading = false;
                        });
                }
            });
        });
    }

    onUpdateSave(): void {
        this.isLoading = true;
        this.update
            .param('goal', this.goal.id)
            .save()
            .then(() => {
                this.reResolve();
            })
            .catch((err) => {
                this.notify.error(err);
                this.isLoading = false;
            });
    }

    onDelete(): void {
        setTimeout(() => {
            this.confirmDelete.show().then((confirmDelete) => {
                if (confirmDelete) {
                    this.isLoading = true;
                    this.goal
                        .delete()
                        .then(() => {
                            this.router.navigate(['../'], { relativeTo: this.route });
                        })
                        .catch((err) => {
                            this.notify.error(err);
                            this.isLoading = false;
                        });
                }
            });
        });
    }

    private renderProgress(): void {
        setTimeout(() => {
            // Shared progress bar
            $(this.element.nativeElement)
                .find('.ui.progress.shared')
                .progress({
                    showActivity: false,
                    onSuccess: function (): void {
                        let percent = 100;
                        if (+$(this).progress('get value') > +$(this).progress('get total')) {
                            percent = Math.round(
                                (+$(this).progress('get value') / +$(this).progress('get total')) * 100
                            );
                        }
                        $(this).progress('set bar label', percent.toString() + '%');
                    },
                    total: this.goal.sharedMetricValue,
                    value: this.goal.currentValue,
                });
            try {
                if (this.goal.sharedMetricValue >= this.goal.currentValue) {
                    this.sharedPercent = Math.round((this.goal.currentValue / this.goal.sharedMetricValue) * 100);
                } else {
                    this.sharedPercent = 100;
                }
            } catch (e) {
                this.sharedPercent = 0;
                console.warn(e);
            }

            // Individual progress bar
            if ($(this.element.nativeElement).find('.ui.progress.individual').length) {
                $(this.element.nativeElement)
                    .find('.ui.progress.individual')
                    .progress({
                        showActivity: false,
                        onSuccess: function (): void {
                            let percent = 100;
                            if (+$(this).progress('get value') > +$(this).progress('get total')) {
                                percent = Math.round(
                                    (+$(this).progress('get value') / +$(this).progress('get total')) * 100
                                );
                            }
                            $(this).progress('set bar label', percent.toString() + '%');
                        },
                        total: this.goal.metricValue,
                        value: this.individualProgress.currentValue,
                    });

                try {
                    if (this.goal.metricValue >= this.individualProgress.currentValue) {
                        this.individualPercent = Math.round(
                            (this.individualProgress.currentValue / this.goal.metricValue) * 100
                        );
                        if (Number.isNaN(this.individualPercent)) {
                            this.individualPercent = 0;
                        }
                    } else {
                        this.individualPercent = 100;
                    }
                } catch (e) {
                    this.individualPercent = 0;
                    console.warn(e);
                }
            }
        });
    }

    // Parse the json milestone data into the update
    private parseMilestones(): void {
        if (this.goal.metricType !== 'milestones') {
            return;
        }

        if (typeof this.goal.currentMilestones === 'undefined' || this.goal.currentMilestones === null) {
            this.update.metricMilestones = JSON.parse(JSON.stringify(this.goal.metricMilestones));
        } else if (!this.goal.currentMilestones.length) {
            this.update.metricMilestones = JSON.parse(JSON.stringify(this.goal.metricMilestones));
        } else if (this.goal.currentMilestones.length !== this.goal.metricMilestones.length) {
            this.update.metricMilestones = JSON.parse(JSON.stringify(this.goal.metricMilestones));
        } else {
            this.update.metricMilestones = JSON.parse(JSON.stringify(this.goal.currentMilestones));
        }
    }

    /**
     * Force complete a goal
     */
    private updateGoalSetCompletedAt(): void {
        this.goal.completedAt = moment().format('YYYY-MM-DD HH:mm:ss');
        this.goal
            .save()
            .then(() => {
                this.notify.success('performance.goal-show.edited');
                this.reResolve();
            })
            .catch((err) => {
                this.notify.error(err);
                this.isLoading = false;
            });
    }
}
